import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { getCycleDateRange } from '@/utils/cycle-dates';
import type { Member } from '@shared/api.interface';
import {
  Crown,
  Package,
  ShieldCheck,
} from 'lucide-react';

interface MemberBenefitsDialogProps {
  session: {
    passcode: string;
    member: Member;
    remainingCycle: number;
    remainingToday: number;
  };
  triggerClassName?: string;
}

function getStatusText(status: Member['status']): string {
  if (status === 'active') return '正常生效';
  if (status === 'expired') return '已过期';
  return '已冻结';
}

export function MemberBenefitsDialog({
  session,
  triggerClassName,
}: MemberBenefitsDialogProps) {
  const [open, setOpen] = useState(false);
  const isTotalMode = session.member.quota_mode === 'total';
  const showDailyLimit = session.member.daily_limit > 0;
  const totalQuota = session.member.total_quota ?? 0;
  const quotaLimit = isTotalMode ? totalQuota : session.member.monthly_quota;
  const usedCount = isTotalMode ? session.member.total_redemptions : session.member.cycle_used;
  const remainingLabel = isTotalMode ? '还可领取' : '本周期还可领';
  const usedLabel = isTotalMode ? '已领取 / 可领总数' : '本周期已领 / 周期总额';
  const metricsGridClass = isTotalMode
    ? 'grid grid-cols-2 gap-2 sm:gap-2.5'
    : 'grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-2.5';

  const cycleDates = useMemo(
    () => (!isTotalMode ? getCycleDateRange(session.member.cycle_start_date) : null),
    [isTotalMode, session.member.cycle_start_date]
  );

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className={triggerClassName}
        onClick={() => setOpen(true)}
      >
        <ShieldCheck className="mr-1 h-4 w-4" />
        会员权益
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-xl overflow-hidden p-0">
          <DialogHeader className="border-b bg-gradient-to-r from-amber-100 via-orange-50 to-white px-4 pb-3 pt-4 text-left sm:px-6 sm:pb-4 sm:pt-6">
            <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <Crown className="h-5 w-5 text-primary" />
              会员权益中心
            </DialogTitle>
            <DialogDescription>
              {isTotalMode
                ? '先看还可领取次数和到期时间。'
                : '先看本周期还可领次数和到期时间。'}
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="max-h-[calc(90vh-88px)]">
            <div className="px-4 py-4 sm:px-6 sm:py-5">
              <div className="space-y-3">
                <Card className="overflow-hidden border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50 shadow-sm">
                  <CardContent className="p-4 sm:p-5">
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                      <Badge variant="secondary" className="bg-amber-100 text-amber-900">
                        {session.member.member_type}
                      </Badge>
                      <Badge variant={session.member.status === 'active' ? 'default' : 'destructive'}>
                        {getStatusText(session.member.status)}
                      </Badge>
                      <span className="min-w-0 text-xs text-muted-foreground">
                        兑换码：<span className="break-all font-medium text-foreground">{session.passcode}</span>
                      </span>
                    </div>

                    <div className={metricsGridClass}>
                      <div className="rounded-xl border border-amber-200 bg-white/80 px-2.5 py-2 sm:px-3 sm:py-2.5">
                        <p className="text-xs text-muted-foreground sm:text-sm">{remainingLabel}</p>
                        <p className="mt-0.5 text-lg font-semibold text-foreground sm:text-2xl">{session.remainingCycle}</p>
                      </div>

                      <div className="rounded-xl border border-amber-200 bg-white/80 px-2.5 py-2 sm:px-3 sm:py-2.5">
                        <p className="text-xs text-muted-foreground sm:text-sm">{usedLabel}</p>
                        <p className="mt-0.5 text-lg font-semibold text-foreground sm:text-2xl">{usedCount} / {quotaLimit}</p>
                      </div>

                      {!isTotalMode && (
                        <div className="col-span-2 rounded-xl border border-amber-200 bg-white/80 px-2.5 py-2 sm:col-span-1 sm:px-3 sm:py-2.5">
                          <p className="text-xs text-muted-foreground sm:text-sm">今日可领</p>
                          <p className="mt-0.5 text-lg font-semibold text-foreground sm:text-2xl">{session.remainingToday}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-3.5 sm:p-4">
                    <p className="mb-2.5 text-sm font-medium text-foreground">时间信息</p>
                    <div className="space-y-1.5 sm:grid sm:grid-cols-3 sm:gap-2.5 sm:space-y-0">
                      <div className="flex items-center justify-between gap-2 rounded-lg border bg-muted/20 px-2.5 py-1.5 sm:flex-col sm:items-start sm:justify-start sm:px-3 sm:py-2">
                        <p className="text-xs text-muted-foreground">开通时间</p>
                        <p className="text-xs font-medium text-foreground sm:mt-1 sm:text-sm">{session.member.start_date || '-'}</p>
                      </div>
                      <div className="flex items-center justify-between gap-2 rounded-lg border bg-muted/20 px-2.5 py-1.5 sm:flex-col sm:items-start sm:justify-start sm:px-3 sm:py-2">
                        <p className="text-xs text-muted-foreground">到期时间</p>
                        <p className="text-xs font-medium text-foreground sm:mt-1 sm:text-sm">{session.member.expiry_date || '-'}</p>
                      </div>
                      <div className="flex items-center justify-between gap-2 rounded-lg border bg-muted/20 px-2.5 py-1.5 sm:flex-col sm:items-start sm:justify-start sm:px-3 sm:py-2">
                        <p className="text-xs text-muted-foreground">最后领取时间</p>
                        <p className="text-xs font-medium text-foreground sm:mt-1 sm:text-sm">{session.member.last_redemption_date || '暂无'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-3.5 sm:p-4">
                    <div className="mb-2.5 flex items-center gap-2">
                      <Package className="h-4 w-4 text-muted-foreground" />
                      <p className="text-sm font-medium text-foreground">领取说明</p>
                    </div>
                    <div className="space-y-1.5 text-sm text-muted-foreground">
                      {isTotalMode ? (
                        <>
                          <p>在有效期内共可领取 {totalQuota} 次。</p>
                          <p>当前已领取 {session.member.total_redemptions} 次，还可领取 {session.remainingCycle} 次。</p>
                        </>
                      ) : (
                        <>
                          <p>每 30 天会恢复可领取次数。</p>
                          <p>当前周期：{cycleDates ? `${cycleDates.startLabel} - ${cycleDates.endLabel}` : '-'}</p>
                          <p>下次恢复：{cycleDates?.nextResetDate || '-'}</p>
                          <p>{showDailyLimit ? `单日上限：${session.member.daily_limit} 次。` : '单日不限。'}</p>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}
